/* eslint-disable max-len */
// React Imports
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

// Spectrum
import {
  ActionButton,
  Content,
  Flex,
  Grid,
  Tooltip,
  TooltipTrigger,
  View,
} from '@adobe/react-spectrum';
import Help from '@spectrum-icons/workflow/HelpOutline';

// SEO
import { Helmet } from 'react-helmet';

// I18n
import { I18n } from 'react-redux-i18n';

// Framer Motion
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import { PageTransitions, PageVariants } from '../../util/FramerMotionConfig';

// withRouter
import { withRouter } from '../../components/withRouter';

// Local project components
/* commenting this code to remove company-size filter from view  */
/* import CompanySizeFilter from '../CompanySizeFilter'; */
import HeroBlade from '../../components/Filters/HeroBlade';
// Redux Actions
import { initializeFilters } from '../../actions';
import {
  addSingleFilter, getMoreResults, removeSingleFilter,
} from '../../actions/filterActions';

// Styles
import '../../styles/ListPage.scss';
import FilterContainer from '../../components/Filters/FilterContainer';
import LoadMore from '../../components/Filters/LoadMore';
import Search from '../../components/Filters/Search';
import {
  ADD_SINGLE_FILTER,
  CLEAR_ALL_FILTERS, REMOVE_SINGLE_FILTER, SET_COMPANY_DETAILS, SET_FACET_OPTIONS, SET_IS_OPEN, SET_PROGRAM_TYPE,
} from '../../constants';
import ProgramTypeConfig from '../../util/programTypeConfig';
import ContactUs from './Action/ContactUs';
import SelectedFilterTag from '../../components/Filters/SelectedFilterTag';
import MobileFilterContainer from '../../components/Filters/MobileFilterContainer';
import {
  renderCards, renderLoadingIndicator, renderResultsCounter, renderSortFilter,
} from './utils';

const ListPage = ({
  initializeFiltersAction,
  initialized,
  router,
  cards,
  loadingCards,
  loadingMoreCards,
  totalResults,
  getMoreResultsAction,
  loadingResults,
  appInitialized,
  appliedFilters,
  programType,
  programPath,
  isOpen,
  companyDetails,
  isMobile,
  facets,
}) => {
  const dispatch = useDispatch();
  const [disableTabSelection, setDisableTabSelection] = useState(false);

  function updateLevelOptions() {
    let levelOptions = facets.level.options;
    if (programType === 'technology') {
      levelOptions = levelOptions.filter((item) => item.id !== 'Bronze');
    } else if (!levelOptions.some((item) => item.id === 'Bronze')) {
      levelOptions.push({ id: 'Bronze', name: 'Bronze', active: true });
    }
    dispatch({
      type: SET_FACET_OPTIONS,
      payload: { name: 'level.options', value: levelOptions },
    });
  }

  useEffect(() => {
    const expectedPath = programType === 'technology' ? 'mxp' : 'spp';
    if (programType !== expectedPath) {
      setDisableTabSelection(true);
      dispatch({ type: SET_PROGRAM_TYPE, payload: { programPath: programType === 'technology' ? 'mxp' : 'spp' } });
      dispatch({ type: CLEAR_ALL_FILTERS });
      updateLevelOptions();
      if (programType === 'technology') {
        dispatch({
          type: REMOVE_SINGLE_FILTER,
          payload: {
            name: 'specialized',
          },
        });
      }
      if (programType === 'solution') {
        dispatch({
          type: ADD_SINGLE_FILTER,
          payload: { name: 'specialized', value: true },
        });
      }
      initializeFiltersAction(router);
    }
  }, [programType]);

  const loadMoreResults = (entries) => {
    if (entries[0].isIntersecting && initialized
      && !loadingCards && totalResults > Object.keys(cards).length) {
      getMoreResultsAction();
    }
  };

  const handleContactUsModal = (id, name) => {
    dispatch({ type: SET_IS_OPEN, payload: !isOpen });
    dispatch({
      type: SET_COMPANY_DETAILS,
      payload: {
        id,
        name,
      },
    });
  };

  const handleContactUsClear = () => {
    dispatch({ type: SET_IS_OPEN, payload: false });
    dispatch({
      type: SET_COMPANY_DETAILS,
      payload: {},
    });
  };

  const handleSortChange = (val) => {
    if (val && val !== 'default') {
      dispatch(addSingleFilter({ name: 'sort', value: val }, router));
    } else {
      dispatch(removeSingleFilter({ name: 'sort' }, router));
    }
  };

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver((entries) => loadMoreResults(entries), {
      rootMargin: '0px 0px 200px 0px',
    });
    const target = document.querySelector('#infinite-scroll-target');

    if (target) {
      intersectionObserver.observe(target);
    }

    if (!loadingCards && disableTabSelection) {
      setDisableTabSelection(false);
    }

    return () => {
      if (target) {
        intersectionObserver.unobserve(target);
      }
    };
  }, [cards, totalResults, initialized, loadingCards]);

  return (
    <>
      <Helmet titleTemplate={`%s | ${I18n.t('partner_finder.seo.app_title')}`}>
        <title>{I18n.t('partner_finder.seo.filter_page_title')}</title>
        <meta name="description" content={I18n.t('partner_finder.seo.filter_page_description')} />
        <meta property="og:title" content={`${I18n.t('partner_finder.seo.filter_page_title')} | ${I18n.t('partner_finder.seo.app_title')}`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={I18n.t('partner_finder.seo.app_title')} />
        <meta property="og:image" content={`${process.env.PUBLIC_URL}/img/thumb.png`} />
        <meta property="og:description" content={I18n.t('partner_finder.seo.filter_page_description')} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="480" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${I18n.t('partner_finder.seo.filter_page_title')} | ${I18n.t('partner_finder.seo.app_title')}`} />
        <meta name="twitter:description" content={I18n.t('partner_finder.seo.filter_page_description')} />
        <meta name="twitter:image" content={`${process.env.PUBLIC_URL}/img/thumb.png`} />
        <meta name="twitter:image:alt" content="Adobe" />
        <meta name="twitter:site" content="@Adobe" />
        <meta name="twitter:creator" content="@Adobe" />
      </Helmet>
      <motion.div
        exit="out"
        animate="in"
        initial="out"
        variants={PageVariants}
        transition={PageTransitions}
      >
        <HeroBlade isMobile={isMobile} disableTabSelection={disableTabSelection} />
        <div className="mainContainer">
          {isMobile ? (
            <>
              <View id="mobile-result-search-section">
                <div className="mobileFilterSection">
                  <Flex>
                    <Search />
                    <TooltipTrigger delay={0} placement="top right">
                      <ActionButton isQuiet>
                        <Help aria-label="Information" size="S" />
                      </ActionButton>
                      <Tooltip>
                        <Content UNSAFE_className="multi-select-tooltip-content">
                          <a href={programPath === 'mxp' ? window.directoryConfig.findTppAdobePartner : window.directoryConfig.findSppAdobePartner} target="_blank" rel="noreferrer">Learn more </a>
                          {`about  ${ProgramTypeConfig[programPath].text}`}
                        </Content>
                      </Tooltip>
                    </TooltipTrigger>
                  </Flex>
                </div>
                <Flex alignItems="center" justifyContent="space-between" marginTop={16}>
                  <MobileFilterContainer />
                  <AnimatePresence>
                    {renderResultsCounter(loadingResults, appInitialized, totalResults)}
                  </AnimatePresence>
                </Flex>

                {renderSortFilter(appliedFilters, isMobile, programPath, handleSortChange)}
              </View>
              <Grid
                id="filter-page-container"
                areas={['content']}
                UNSAFE_style={{
                  gridTemplateColumns: '90%',
                }}
                margin="0px 10%"
              >
                <View gridArea="content">
                  <Flex UNSAFE_className="card-container">
                    <AnimatePresence>
                      {renderLoadingIndicator(loadingCards, loadingMoreCards)}
                    </AnimatePresence>
                    {renderCards(cards, loadingCards, initialized, router, handleContactUsModal)}
                  </Flex>
                  <div id="infinite-scroll-target" />
                  <LoadMore />
                </View>
              </Grid>
            </>
          ) : (
            <Grid
              id="filter-page-container"
              areas={['filter  content']}
              UNSAFE_style={{
                gridTemplateColumns: '17% 83%',
              }}
              margin="30px 3% 30px 3%"
            >
              <View gridArea="filter">
                <FilterContainer />
              </View>
              <View gridArea="content">
                <View id="result-search-section">
                  <div className="filterSection">
                    <Search />
                    <TooltipTrigger delay={0} placement="top right">
                      <ActionButton isQuiet>
                        <Help aria-label="Information" size="S" />
                      </ActionButton>
                      <Tooltip>
                        <Content UNSAFE_className="multi-select-tooltip-content">
                          <a href={programPath === 'mxp' ? window.directoryConfig.findTppAdobePartner : window.directoryConfig.findSppAdobePartner} target="_blank" rel="noreferrer">Learn more </a>
                          {`about  ${ProgramTypeConfig[programPath].text}`}
                        </Content>
                      </Tooltip>
                    </TooltipTrigger>
                    <AnimatePresence>
                      {renderResultsCounter(loadingResults, appInitialized, totalResults)}
                    </AnimatePresence>
                  </div>
                  {renderSortFilter(appliedFilters, isMobile, programPath, handleSortChange)}
                </View>
                <SelectedFilterTag />
                <Flex UNSAFE_className="card-container">
                  <AnimatePresence>
                    {renderLoadingIndicator(loadingCards, loadingMoreCards)}
                  </AnimatePresence>
                  {renderCards(cards, loadingCards, initialized, router, handleContactUsModal)}
                </Flex>
                <div id="infinite-scroll-target" />
                <LoadMore />
              </View>
            </Grid>
          )}
        </div>
        <ContactUs
          actionType="contactUs"
          initialData={{}}
          companyDetails={companyDetails}
          isOpen={isOpen}
          handleContactUsClear={handleContactUsClear}
          programPath={programPath}
        />
      </motion.div>
    </>
  );
};

const mapStateToProps = (state) => ({
  locale: state.i18n.locale,
  cards: state.results.cards,
  loadingCards: state.results.loadingResults,
  loadingMoreCards: state.results.loadingMoreResults,
  totalResults: state.results.totalResults,
  initialized: state.facets.initialized,
  loadingResults: state.results.loadingResults,
  appInitialized: state.facets.initialized,
  appliedFilters: state.filter.selected,
  programPath: state.facets.programPath,
  isOpen: state.contactUs.isOpen,
  companyDetails: state.contactUs.companyDetails,
  isMobile: state.facets.isMobile,
  facets: state.facets,
});

const mapDispatchToProps = {
  initializeFiltersAction: initializeFilters,
  getMoreResultsAction: getMoreResults,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ListPage));
